import { getCachedUser, setCachedUser } from "./cached-user"

/**
 * Heaps session access token. Deliberately not called `accessToken` - that name
 * is common enough that a provider or campaign link could append one of its
 * own, and honouring a foreign token would replace the visitor's session.
 */
export const HANDOVER_ACCESS_TOKEN_PARAM = "hsat"

/**
 * Superseded by `hsat`. Still read so that payments already in flight when this
 * shipped complete correctly - safe to delete once none can be outstanding.
 */
const LEGACY_ACCESS_TOKEN_PARAM = "accessToken"

// Only the post-payment redirect issues the handover, and it is the only route
// allowed to consume it.
const HANDOVER_PATHNAME = /\/checkout\/[^/]+\/redirect\/?$/

/**
 * The session that owns the order, handed over on the post-payment redirect.
 *
 * The redirect back from a payment provider can land in a different browser
 * than the one that started checkout - iOS gives the return URL to the default
 * browser, not the originating one - so the owning session's token travels in
 * the URL as `?hsat=` (see routes.checkout.redirect).
 *
 * Adopted by AccountProvider, which would otherwise bootstrap a guest session
 * on that route: that clears hg::cache::order and replaces the owner's token,
 * leaving the redirect unable to read its own order.
 *
 * Non-null only on the post-payment redirect.
 */
export const getHandoverAccessToken = () => {
  // location is absent under SSR and in tests that stub a bare window
  if (typeof window === "undefined" || !window.location) {
    return null
  }

  if (!HANDOVER_PATHNAME.test(window.location.pathname)) {
    return null
  }

  const searchParams = new URLSearchParams(window.location.search)

  return (
    searchParams.get(HANDOVER_ACCESS_TOKEN_PARAM) ??
    searchParams.get(LEGACY_ACCESS_TOKEN_PARAM)
  )
}

let hasAdopted = false

/**
 * Store the handed-over session so the redirect reads its own order.
 *
 * Called before every session-backed request rather than from a React effect:
 * the token has to be in place before the first request leaves, and an effect
 * runs too late for anything that fires on mount.
 *
 * The URL wins over whatever is cached - the visitor may still hold a session
 * from before checkout, and that one does not own the order. Once per page
 * load, so a session cleared later is not silently replaced by the URL again.
 *
 * No-op off the post-payment redirect (see getHandoverAccessToken).
 */
export const adoptHandoverSession = () => {
  if (hasAdopted) {
    return
  }

  const handoverAccessToken = getHandoverAccessToken()
  if (!handoverAccessToken) {
    return
  }
  hasAdopted = true

  // A full session already matching the handover carries is_signed_up, which
  // the bare token would drop - that would read the owner back as a guest.
  if (getCachedUser()?.access_token === handoverAccessToken) {
    return
  }

  setCachedUser({ access_token: handoverAccessToken })
}
